import { useState, useEffect, useRef, useCallback } from 'react'
import { fetchFlights } from '../lib/api'

const EARTH_RADIUS_NM = 3440.065

function distanceNm(lat1, lon1, lat2, lon2) {
  const toRad = d => d * Math.PI / 180
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
  return EARTH_RADIUS_NM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

// Initial bearing from (lat1,lon1) to (lat2,lon2), in degrees - this is the
// direction to look from the viewer's location, distinct from an aircraft's
// own true_track (the direction it's flying).
function bearingDeg(lat1, lon1, lat2, lon2) {
  const toRad = d => d * Math.PI / 180
  const y = Math.sin(toRad(lon2 - lon1)) * Math.cos(toRad(lat2))
  const x = Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) - Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(toRad(lon2 - lon1))
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360
}

export function useFlightData(location, radiusKm = 20, updateInterval = 10000) {
  const [flights, setFlights] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [stats, setStats] = useState({ total: 0, avgAltitude: 0, avgSpeed: 0 })
  const intervalRef = useRef(null)
  const hasLoadedOnce = useRef(false)

  const refresh = useCallback(async () => {
    if (!location.lat || !location.lon) return

    if (!hasLoadedOnce.current) setLoading(true)
    try {
      const data = await fetchFlights(null, { lat: location.lat, lon: location.lon, km: radiusKm })

      if (data && data.states) {
        const parsed = data.states.map(s => {
          const latitude = s[6], longitude = s[5]
          const hasPosition = latitude != null && longitude != null
          return {
            icao24: s[0],
            callsign: s[1]?.trim() || 'Unknown',
            origin_country: s[2],
            latitude,
            longitude,
            altitude: s[7],
            velocity: s[9],
            true_track: s[10],
            vertical_rate: s[11],
            on_ground: s[8],
            aircraftTypeCode: s[12] || null,
            aircraftType: s[13] || null,
            distance: hasPosition ? Math.round(distanceNm(location.lat, location.lon, latitude, longitude) * 10) / 10 : null,
            bearing: hasPosition ? bearingDeg(location.lat, location.lon, latitude, longitude) : null,
          }
        })
        setFlights(parsed)
        setStats({
          total: parsed.length,
          avgAltitude: parsed.length ? parsed.reduce((a, f) => a + (f.altitude || 0), 0) / parsed.length : 0,
          avgSpeed: parsed.length ? parsed.reduce((a, f) => a + (f.velocity || 0), 0) / parsed.length : 0,
        })
        setError(null)
      } else {
        setFlights([])
        setStats({ total: 0, avgAltitude: 0, avgSpeed: 0 })
        setError('Live flight data unavailable')
      }
    } catch (err) {
      setFlights([])
      setStats({ total: 0, avgAltitude: 0, avgSpeed: 0 })
      setError('Live flight data unavailable')
    } finally {
      setLoading(false)
      hasLoadedOnce.current = true
    }
  }, [location.lat, location.lon, radiusKm])

  useEffect(() => {
    refresh()
    intervalRef.current = setInterval(refresh, updateInterval)
    return () => clearInterval(intervalRef.current)
  }, [refresh, updateInterval])

  return { flights, loading, error, stats, refresh }
}
